import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowRight,
  Search as SearchIcon,
  FileText,
  Scale,
  Building2,
  Calendar,
  Filter,
  X,
  Mail,
  Paperclip,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTRACT_STATUSES = {
  draft: { label: 'مسودة', color: 'bg-slate-100 text-slate-700' },
  review: { label: 'قيد المراجعة', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'معتمد', color: 'bg-emerald-100 text-emerald-700' },
  signed: { label: 'موقّع', color: 'bg-purple-100 text-purple-700' },
};

const CASE_STATUSES = {
  new: { label: 'جديدة', color: 'bg-slate-100 text-slate-700' },
  filed: { label: 'مرفوعة', color: 'bg-blue-100 text-blue-700' },
  hearings: { label: 'جلسات', color: 'bg-orange-100 text-orange-700' },
  final_judgment: { label: 'حكم نهائي', color: 'bg-emerald-100 text-emerald-700' },
  execution: { label: 'تنفيذ', color: 'bg-purple-100 text-purple-700' },
};

const LETTER_TYPES = {
  outgoing: { label: 'صادر', color: 'bg-blue-100 text-blue-700' },
  incoming: { label: 'وارد', color: 'bg-green-100 text-green-700' },
};

export default function Search({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [type, setType] = useState(searchParams.get('type') || 'all');
  const [companyId, setCompanyId] = useState(searchParams.get('company') || 'all');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  
  // Helper function to navigate based on user role and item type
  const navigateToItem = (itemType, companyId, tab) => {
    if (user?.role === 'legal_manager') {
      navigate(`/legal/company/${companyId}?tab=${tab}`);
    } else {
      // For executive, stay on executive dashboard with appropriate tab
      navigate(`/executive?tab=${tab}`);
    }
  };
  
  const [companies, setCompanies] = useState([]);
  const [results, setResults] = useState({ contracts: [], cases: [], agreements: [], commitments: [], letters: [], annexes: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    fetchCompanies();
    // If there's a query in URL, perform search
    if (searchParams.get('q')) {
      performSearch();
    }
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch companies');
    }
  };

  const performSearch = useCallback(async () => {
    if (!query || query.length < 2) {
      toast.error('أدخل كلمة بحث (حرفين على الأقل)');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (type !== 'all') params.append('type', type);
      if (companyId !== 'all') params.append('company_id', companyId);
      if (status !== 'all') params.append('status', status);

      const response = await axios.get(`${API}/search?${params.toString()}`);
      setResults(response.data);

      // Update URL
      setSearchParams({
        q: query,
        ...(type !== 'all' && { type }),
        ...(companyId !== 'all' && { company: companyId }),
        ...(status !== 'all' && { status }),
      });
    } catch (error) {
      toast.error('فشل البحث');
    } finally {
      setLoading(false);
    }
  }, [query, type, companyId, status]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  const clearFilters = () => {
    setType('all');
    setCompanyId('all');
    setStatus('all');
  };

  const handleBack = () => {
    if (user?.role === 'legal_manager') {
      navigate('/companies');
    } else {
      navigate('/executive');
    }
  };

  const getStatusOptions = () => {
    if (type === 'contracts') {
      return Object.entries(CONTRACT_STATUSES).map(([value, { label }]) => (
        <SelectItem key={value} value={value}>{label}</SelectItem>
      ));
    } else if (type === 'cases') {
      return Object.entries(CASE_STATUSES).map(([value, { label }]) => (
        <SelectItem key={value} value={value}>{label}</SelectItem>
      ));
    } else if (type === 'agreements') {
      return [
        <SelectItem key="draft" value="draft">مسودة</SelectItem>,
        <SelectItem key="review" value="review">قيد المراجعة</SelectItem>,
        <SelectItem key="approved" value="approved">معتمدة</SelectItem>,
        <SelectItem key="signed" value="signed">مُفعّلة</SelectItem>,
      ];
    } else if (type === 'commitments') {
      return [
        <SelectItem key="draft" value="draft">مسودة</SelectItem>,
        <SelectItem key="review" value="review">قيد المراجعة</SelectItem>,
        <SelectItem key="approved" value="approved">معتمد</SelectItem>,
        <SelectItem key="signed" value="signed">موقّع</SelectItem>,
      ];
    } else if (type === 'letters') {
      return Object.entries(LETTER_TYPES).map(([value, { label }]) => (
        <SelectItem key={value} value={value}>{label}</SelectItem>
      ));
    }
    return null;
  };

  const highlightText = (text, searchQuery) => {
    if (!text || !searchQuery) return text;
    const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === searchQuery.toLowerCase() 
        ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
        : part
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowRight size={20} className="ml-2" />
            العودة
          </Button>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
            <SearchIcon className="text-[#E94B7B]" size={40} />
            البحث الشامل
          </h1>
          <p className="text-slate-600">ابحث في العقود والقضايا والاتفاقيات والإقرارات والخطابات والملاحق</p>
        </div>

        {/* Search Box */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4 mb-4">
              <div className="flex-1 relative">
                <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder="ابحث بالعنوان، الطرف الثاني، رقم القضية، المدعي..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pr-10 h-12 text-lg"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={performSearch}
                disabled={loading}
                className="h-12 px-8 bg-[#E94B7B] hover:bg-[#d43a6a]"
                data-testid="search-button"
              >
                {loading ? 'جاري البحث...' : 'بحث'}
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2 text-slate-600">
                <Filter size={18} />
                <span>تصفية:</span>
              </div>
              
              <Select value={type} onValueChange={(v) => { setType(v); setStatus('all'); }}>
                <SelectTrigger className="w-40" data-testid="type-filter">
                  <SelectValue placeholder="النوع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">الكل</SelectItem>
                  <SelectItem value="contracts">العقود</SelectItem>
                  <SelectItem value="cases">القضايا</SelectItem>
                  <SelectItem value="agreements">الاتفاقيات</SelectItem>
                  <SelectItem value="commitments">الإقرارات</SelectItem>
                  <SelectItem value="letters">الخطابات</SelectItem>
                  <SelectItem value="annexes">الملاحق</SelectItem>
                </SelectContent>
              </Select>

              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="w-48" data-testid="company-filter">
                  <SelectValue placeholder="الشركة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الشركات</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name_ar}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {type !== 'all' && (
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="الحالة" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الحالات</SelectItem>
                    {getStatusOptions()}
                  </SelectContent>
                </Select>
              )}

              {(type !== 'all' || companyId !== 'all' || status !== 'all') && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X size={16} className="ml-1" />
                  مسح الفلاتر
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div className="space-y-6">
            {/* Results Summary */}
            <div className="text-slate-600">
              {loading ? (
                <span>جاري البحث...</span>
              ) : (
                <span>
                  تم العثور على <strong className="text-slate-900">{results.total}</strong> نتيجة
                  {results.contracts.length > 0 && (
                    <span> ({results.contracts.length} عقد)</span>
                  )}
                  {results.cases.length > 0 && (
                    <span> ({results.cases.length} قضية)</span>
                  )}
                  {results.agreements && results.agreements.length > 0 && (
                    <span> ({results.agreements.length} اتفاقية)</span>
                  )}
                  {results.commitments && results.commitments.length > 0 && (
                    <span> ({results.commitments.length} إقرار)</span>
                  )}
                  {results.letters && results.letters.length > 0 && (
                    <span> ({results.letters.length} خطاب)</span>
                  )}
                  {results.annexes && results.annexes.length > 0 && (
                    <span> ({results.annexes.length} ملحق)</span>
                  )}
                </span>
              )}
            </div>

            {/* Contracts Results */}
            {results.contracts.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FileText className="text-blue-600" size={24} />
                  العقود ({results.contracts.length})
                </h2>
                <div className="space-y-3">
                  {results.contracts.map((contract) => (
                    <Card
                      key={contract.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/contract/${contract.id}`)}
                      data-testid={`contract-result-${contract.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(contract.title, query)}
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(contract.company_name, query)}
                              </span>
                              <span>الطرف الثاني: {highlightText(contract.party_name, query)}</span>
                              <span>النوع: {highlightText(contract.contract_type, query)}</span>
                              {contract.created_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(contract.created_at).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${CONTRACT_STATUSES[contract.status]?.color || 'bg-slate-100'}`}>
                            {CONTRACT_STATUSES[contract.status]?.label || contract.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Cases Results */}
            {results.cases.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Scale className="text-[#E94B7B]" size={24} />
                  القضايا ({results.cases.length})
                </h2>
                <div className="space-y-3">
                  {results.cases.map((caseItem) => (
                    <Card
                      key={caseItem.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/case/${caseItem.id}`)}
                      data-testid={`case-result-${caseItem.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(caseItem.title, query)} 
                              <span className="text-slate-500 font-normal mr-2">
                                ({highlightText(caseItem.case_number, query)})
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(caseItem.company_name, query)}
                              </span>
                              <span>المدعي: {highlightText(caseItem.plaintiff, query)}</span>
                              <span>المدعى عليه: {highlightText(caseItem.defendant, query)}</span>
                              <span>النوع: {highlightText(caseItem.case_type, query)}</span>
                              {caseItem.created_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(caseItem.created_at).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${CASE_STATUSES[caseItem.status]?.color || 'bg-slate-100'}`}>
                            {CASE_STATUSES[caseItem.status]?.label || caseItem.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Agreements Results */}
            {results.agreements && results.agreements.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FileText className="text-indigo-600" size={24} />
                  الاتفاقيات ({results.agreements.length})
                </h2>
                <div className="space-y-3">
                  {results.agreements.map((agreement) => (
                    <Card
                      key={agreement.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigateToItem('agreement', agreement.company_id, 'agreements')}
                      data-testid={`agreement-result-${agreement.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(agreement.title, query)}
                              <span className="text-slate-500 font-normal mr-2">
                                ({highlightText(agreement.agreement_number, query)})
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(agreement.company_name, query)}
                              </span>
                              <span>الطرف الثاني: {highlightText(agreement.party_name, query)}</span>
                              {agreement.agreement_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(agreement.agreement_date).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${CONTRACT_STATUSES[agreement.status]?.color || 'bg-slate-100'}`}>
                            {agreement.status === 'signed' ? 'مُفعّلة' : CONTRACT_STATUSES[agreement.status]?.label || agreement.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Commitments Results */}
            {results.commitments && results.commitments.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Scale className="text-teal-600" size={24} />
                  إقرارات الموظفين ({results.commitments.length})
                </h2>
                <div className="space-y-3">
                  {results.commitments.map((commitment) => (
                    <Card
                      key={commitment.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigateToItem('commitment', commitment.company_id, 'commitments')}
                      data-testid={`commitment-result-${commitment.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(commitment.employee_name, query)}
                              <span className="text-slate-500 font-normal mr-2">
                                ({highlightText(commitment.commitment_number, query)})
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(commitment.company_name, query)}
                              </span>
                              <span>نوع الإقرار: {highlightText(commitment.commitment_type, query)}</span>
                              {commitment.commitment_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(commitment.commitment_date).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${CONTRACT_STATUSES[commitment.status]?.color || 'bg-slate-100'}`}>
                            {commitment.status === 'signed' ? 'موقّع' : CONTRACT_STATUSES[commitment.status]?.label || commitment.status}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Letters Results */}
            {results.letters && results.letters.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Mail className="text-purple-600" size={24} />
                  الخطابات ({results.letters.length})
                </h2>
                <div className="space-y-3">
                  {results.letters.map((letter) => (
                    <Card
                      key={letter.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigateToItem('letter', letter.company_id, 'letters')}
                      data-testid={`letter-result-${letter.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(letter.subject, query)}
                              <span className="text-slate-500 font-normal mr-2">
                                ({highlightText(letter.letter_number, query)})
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(letter.company_name, query)}
                              </span>
                              {letter.sender && <span>المرسل: {highlightText(letter.sender, query)}</span>}
                              {letter.recipient && <span>المرسل إليه: {highlightText(letter.recipient, query)}</span>}
                              {letter.letter_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(letter.letter_date).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${LETTER_TYPES[letter.letter_type]?.color || 'bg-slate-100'}`}>
                            {LETTER_TYPES[letter.letter_type]?.label || letter.letter_type}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Annexes Results */}
            {results.annexes && results.annexes.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Paperclip className="text-orange-600" size={24} />
                  الملاحق ({results.annexes.length})
                </h2>
                <div className="space-y-3">
                  {results.annexes.map((annex) => (
                    <Card
                      key={annex.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigateToItem('annex', annex.company_id, 'agreements')}
                      data-testid={`annex-result-${annex.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-bold text-lg text-slate-900 mb-1">
                              {highlightText(annex.title, query)}
                              <span className="text-slate-500 font-normal mr-2">
                                ({highlightText(annex.annex_number, query)})
                              </span>
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Building2 size={14} />
                                {highlightText(annex.company_name, query)}
                              </span>
                              {annex.agreement_title && (
                                <span>الاتفاقية: {highlightText(annex.agreement_title, query)}</span>
                              )}
                              {annex.annex_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {new Date(annex.annex_date).toLocaleDateString('ar-SA')}
                                </span>
                              )}
                            </div>
                            {annex.description && (
                              <p className="text-sm text-slate-500 mt-1">{highlightText(annex.description, query)}</p>
                            )}
                          </div>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            ملحق
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {!loading && results.total === 0 && (
              <div className="text-center py-16">
                <SearchIcon size={64} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-bold text-slate-700 mb-2">لا توجد نتائج</h3>
                <p className="text-slate-500">جرب كلمات بحث مختلفة أو قم بتغيير الفلاتر</p>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {!searched && (
          <div className="text-center py-16">
            <SearchIcon size={64} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">ابدأ البحث</h3>
            <p className="text-slate-500">أدخل كلمة البحث واضغط على زر البحث</p>
          </div>
        )}
      </div>
    </div>
  );
}
